import { produce } from 'immer';
import { createContext, JSX, useCallback, useMemo, useState } from 'react';
import { customEvents } from '../../../common/constants';
import useMount from '../../../common/hooks/useMount';
import { customerService } from '../services/customer';
import { AvailableCard, Budget } from './common';

export const CustomerBudgetContext = createContext(
  {} as {
    isCustomerBalanceLoading: boolean;
    customerBudget: Budget;
    setCustomerBudget: (customerInfo: Budget) => void;
    loadCustomerBalance: (abortController?: AbortController) => void;
    updateCustomerBudgetCashback: (value: number) => void;
  },
);

export const CustomerBudgetProvider = (props: { children: JSX.Element }) => {
  const [customerBudget, setCustomerBudget] = useState({} as Budget);
  const [isCustomerBalanceLoading, setIsCustomerBalanceLoading] =
    useState(true);

  const updateCustomerBudgetCashback = useCallback((value: number) => {
    setCustomerBudget(
      produce(draft => {
        draft.benefitBucksBalance = value;
      }),
    );
  }, []);

  const loadCustomerBalance = useCallback(
    async (abortController?: AbortController) => {
      setIsCustomerBalanceLoading(true);
      const result = await customerService.getCustomerBalanceSummary({
        signal: abortController?.signal,
      });

      if (!result.hasErrors) {
        setCustomerBudget(
          produce(draft => {
            draft.benefitBudgetBalance = result.data.benefitBudgetBalance;
            draft.benefitBucksBalance = result.data.benefitBucksBalance;
            draft.budgetAddedThisMonth = result.data.budgetAddedThisMonth;
            draft.taxesThisMonth = result.data.taxesThisMonth;
            draft.budgetExpirationDate = result.data.budgetExpirationDate;
            draft.availableCards = result.data.availableCards?.map(
              (c: AvailableCard) => ({
                cardType: c.cardType,
                balance: c.balance,
                isIntegrationAvailable: c.isIntegrationAvailable,
              }),
            );
          }),
        );
      }
      setIsCustomerBalanceLoading(false);
    },
    [],
  );

  useMount(abortController => {
    loadCustomerBalance(abortController);
    document.addEventListener(customEvents.refreshCustomerBalances, () => {
      loadCustomerBalance(abortController);
    });
  });

  return (
    <CustomerBudgetContext.Provider
      value={useMemo(() => {
        return {
          customerBudget,
          setCustomerBudget,
          loadCustomerBalance,
          isCustomerBalanceLoading,
          updateCustomerBudgetCashback,
        };
      }, [
        customerBudget,
        loadCustomerBalance,
        isCustomerBalanceLoading,
        updateCustomerBudgetCashback,
      ])}
    >
      {props.children}
    </CustomerBudgetContext.Provider>
  );
};
